import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {openInBrowser} from '../../../../srcCommon/browser/openInBrowser';
import errorImgSrc from '../../../assets/images/error2.png';
import {CrmSyncSourcesEnum} from '../../../models/api/crmSyncModel';
import {computeHelpCenterLink} from '../crm/common/crmSyncHelpers';

/*
 * Props.
 */

interface CrmPreviewErrorProps {
  crmSyncSource: CrmSyncSourcesEnum;
}

/*
 * Style.
 */

const StyledErrorWrapperDiv = styled.div`
  height: 100%;
  display: flex;
  flex-flow: column;
  vertical-align: middle;
`;

const StyledErrorSpacerDiv = styled.div`
  flex: 1;
`;

const StyledErrorImg = styled.img`
  width: 92px;
  height: 67px;
  margin: auto;
`;

const StyledErrorTitleDiv = styled.div`
  margin-top: 24px;
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.veryLarge};
  text-align: center;
`;

const StyledErrorDescriptionDiv = styled.div`
  margin-top: 8px;
  text-align: center;
  color: ${(p) => p.theme.greys.shade70};
  padding: 0 110px;
  line-height: 21px;
`;

const StyledButtonWrapperDiv = styled.div`
  margin-top: 24px;
  align-self: center;
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 4px;
`;

const StyledButton = styled(Button)`
  display: inline-block;
  margin: auto;
`;

/*
 * Component.
 */

export const CrmPreviewError: FC<CrmPreviewErrorProps> = ({crmSyncSource}) => (
  <StyledErrorWrapperDiv>
    <StyledErrorSpacerDiv />
    <StyledErrorImg src={errorImgSrc} alt="error" />
    <StyledErrorTitleDiv>
      <Trans id="crm-preview-error-title">Something went wrong</Trans>
    </StyledErrorTitleDiv>
    <StyledErrorDescriptionDiv>{renderPreviewDescription(crmSyncSource)}</StyledErrorDescriptionDiv>
    <StyledButtonWrapperDiv>
      <StyledButton
        onClick={() => openInBrowser(computeHelpCenterLink(crmSyncSource))}
        variant={ButtonVariantsEnum.LINK}
      >
        <StyledButtonContentDiv>
          <Trans id="crm-preview-error-learn-more">Learn more</Trans>
          <Icon name="externalLinkSmall" />
        </StyledButtonContentDiv>
      </StyledButton>
    </StyledButtonWrapperDiv>
    <StyledErrorSpacerDiv />
  </StyledErrorWrapperDiv>
);

/*
 * Helpers.
 */

function renderPreviewDescription(crmSyncSource: CrmSyncSourcesEnum) {
  switch (crmSyncSource) {
    case CrmSyncSourcesEnum.SALESFORCE:
      return (
        <Trans id="crm-preview-error-description-salesforce">
          We&apos;re unable to generate a preview because the data in your Salesforce is incompatible with
          Front. Please make the necessary updates in Salesforce before trying to preview or sync again.
        </Trans>
      );
    case CrmSyncSourcesEnum.HUBSPOT:
      return (
        <Trans id="crm-preview-error-description-hubspot">
          We&apos;re unable to generate a preview because the data in your Hubspot is incompatible with Front.
          Please make the necessary updates in Hubspot before trying to preview or sync again.
        </Trans>
      );
    case CrmSyncSourcesEnum.MICROSOFT_DYNAMICS:
      return (
        <Trans id="crm-preview-error-description-dynamics">
          We&apos;re unable to generate a preview because the data in your Dynamics 365 is incompatible with
          Front. Please make the necessary updates in Dynamics 365 before trying to preview or sync again.
        </Trans>
      );
    case CrmSyncSourcesEnum.OUTLOOK_DIRECTORY:
      return (
        <Trans id="crm-preview-error-description-outlook">
          We&apos;re unable to generate a preview because the data in your Outlook directory is incompatible
          with Front. Please make the necessary updates in Outlook directory before trying to preview or sync
          again.
        </Trans>
      );
    default:
      return null;
  }
}
